import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, theme as antdTheme, Grid, Drawer, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import { 
  MenuOutlined, 
  LogoutOutlined, 
  ProjectOutlined,
  UsergroupAddOutlined,
  KeyOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  isMobile?: boolean;
}

const AdminNavigation = React.memo(() => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  return <AdminSidebar isMobile={isMobile} />;
});

const AdminSidebar = React.memo(({ isMobile }: SidebarProps) => {
  const { theme } = useTheme();
  const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
  const location = useLocation();
  const currentPath = location.pathname;
  const isRolePermissionsPage = currentPath === '/admin/role-permissions';
  const currentKey = currentPath === '/admin' 
    ? 'organizations'     
        : currentPath === '/admin/employees'
          ? 'employees'
        : currentPath === '/admin/roles' || isRolePermissionsPage
          ? 'roles'        
        : currentPath.includes('/admin/organization') 
          ? 'organizations'
        : currentPath === '/admin/checklist-configuration'
          ? 'checklist-configuration'
        : currentPath === '/admin/clients'
            ? 'clients'
          : currentPath === '/admin/project-types' || currentPath.includes('/admin/project-types') || currentPath.includes('/admin/project-types/:projectTypeId') || currentPath.includes('/admin/projects')
            ? 'projects'
            : '';
          
  const { logout } = useAuth();
  const { hasAdminPermission } = usePermission();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const toggleDrawer = () => setDrawerVisible(v => !v);

  const handleBackToPrevious = () => {
    navigate('/project-selection');
  };

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({ 
      key: 'back-to-previous', 
      icon: <ArrowLeftOutlined />, 
      label: 'Back to Previous', 
      onClick: handleBackToPrevious,
      style: { marginBottom: '8px', borderBottom: '1px solid #f0f0f0' }
    });

    if (hasAdminPermission('roles')) {
      items.push({ key: 'roles', icon: <KeyOutlined />, label: <Link to={`/admin/roles`}>Roles</Link> });      
    }

    if (hasAdminPermission('projects')) {
      items.push({ key: 'projects', icon: <ProjectOutlined />, label: <Link to={`/admin/project-types`}>Projects</Link> });
    }

    if (hasAdminPermission('organizations')) {
      items.push({ key: 'employees', icon: <UsergroupAddOutlined />, label: <Link to={`/admin/employees`}>Employees</Link> });
    }

    if (hasAdminPermission('clients')) {
      items.push({ key: 'clients', icon: <UsergroupAddOutlined />, label: <Link to={`/admin/clients`}>Clients</Link> });
    }      

    if (hasAdminPermission('checklist-config')) {
      items.push({ key: 'checklist-configuration', icon: <FileTextOutlined />, label: <Link to={`/admin/checklist-configuration`}>Checklist Configuration</Link> });
    }

    items.push({ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout, danger: true });
    
    return items;
  }, [logout, hasAdminPermission, handleBackToPrevious]);

  const menu = (
    <Menu
      selectedKeys={[currentKey]}
      theme={isDarkMode ? 'dark' : 'light'}
      mode="inline"
      items={menuItems}
      onClick={() => isMobile && setDrawerVisible(false)}
      style={{ border: 'none', paddingTop: '16px' }}
    />
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{ position: 'fixed', top: 16, left: 16, zIndex: 1000 }}
        />
        <Drawer
          placement="left"
          closable={false}
          onClose={toggleDrawer}
          open={drawerVisible}
          width="80%"
          bodyStyle={{ padding: 0, background: isDarkMode ? '#141414' : '#fff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography.Text style={{ color: '#ffffff', fontWeight: 700, fontSize: '16px', lineHeight: 1, letterSpacing: '-0.5px' }}>
                CC
              </Typography.Text>
            </div>
            <div>
              <Typography.Text style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: isDarkMode ? '#ffffff' : '#1a1a1a', lineHeight: 1.3 }}>
                Climate Connections
              </Typography.Text>
              <Typography.Text style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', lineHeight: 1.3, marginTop: '2px' }}>
                LLP
              </Typography.Text>
            </div>
          </div>
          {menu}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      width={220}
      theme={isDarkMode ? 'dark' : 'light'}
      style={{ 
        height: '100vh', 
        position: 'fixed', 
        left: 0, 
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        overflow: 'auto'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px' }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Typography.Text style={{ color: '#ffffff', fontWeight: 700, fontSize: '16px', lineHeight: 1, letterSpacing: '-0.5px' }}>
            CC
          </Typography.Text>
        </div>
        <div>
          <Typography.Text style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: isDarkMode ? '#ffffff' : '#1a1a1a', lineHeight: 1.3 }}>
            Climate Connections
          </Typography.Text>
          <Typography.Text style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', lineHeight: 1.3, marginTop: '2px' }}>
            LLP
          </Typography.Text>
        </div>
      </div>
      {menu}
    </Sider>
  );
});

export default AdminNavigation;
